import React, { useEffect, useState } from "react";

import { Modal, ModalInitialFocus } from "../../../../components/Modal/Modal";
import { gameConceptBigIdLength, partnerCenterDashboardUrl } from "../../../../constants";
import { useStrings } from "../../../../localization/localizationHelpers";
import * as styles from "../../GameConceptPage.module.css";

type SubmitGameConceptModalProps = Readonly<{
  isOpen: boolean;
  initialBigId?: string;
  canConfirm: boolean;
  onClose: () => void;
  onConfirm: (bigId: string) => void;
}>;

export const SubmitGameConceptModal = ({
  isOpen,
  initialBigId = "",
  canConfirm,
  onClose,
  onConfirm,
}: SubmitGameConceptModalProps) => {
  const [
    title,
    description,
    label,
    guidance,
    lengthHint,
    partnerCenter,
    submit,
    cancel,
    submitWithoutId,
  ] = useStrings(
    "gameConceptNewSubmissionTitle",
    "gameConceptBigIdDescription",
    "gameConceptBigIdLabel",
    "gameConceptBigIdGuidance",
    "gameConceptBigIdInvalidLength",
    "partnerCenter",
    "xboxOnboardSubmitButton",
    "cancelButton",
    "gameConceptSubmitWithoutStoreId"
  );
  const [bigId, setBigId] = useState(initialBigId);
  const normalizedBigId = bigId.trim();
  const isValidBigId = normalizedBigId.length === gameConceptBigIdLength;
  const [descriptionBeforeLink, descriptionAfterLink] = description.split("{0}");
  useEffect(() => {
    if (isOpen) {
      setBigId(initialBigId);
    }
  }, [initialBigId, isOpen]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      ariaLabelledBy="submit-game-concept-title"
      className={styles.submissionModal}
      initialFocus={ModalInitialFocus.FirstInput}
    >
      <form
        onSubmit={event => {
          event.preventDefault();
          if (canConfirm && isValidBigId) {
            onConfirm(normalizedBigId);
          }
        }}
      >
        <div className={styles.deleteModalHeader}>
          <h2 id="submit-game-concept-title" className={styles.deleteModalTitle}>
            {title}
          </h2>
        </div>
        <p id="submit-game-concept-description" className={styles.submissionDescription}>
          {descriptionBeforeLink}
          <a
            className={styles.idXboxRequirementLink}
            href={partnerCenterDashboardUrl}
            rel="noopener noreferrer"
            target="_blank"
          >
            {partnerCenter}
          </a>
          {descriptionAfterLink}
        </p>
        <label className={styles.submissionBigIdLabel} htmlFor="submit-game-concept-big-id">
          {label}
        </label>
        <p id="submit-game-concept-length" className={styles.submissionDescription}>
          {lengthHint}
        </p>
        <input
          id="submit-game-concept-big-id"
          className={styles.submissionBigIdInput}
          type="text"
          value={bigId}
          onChange={event => setBigId(event.target.value)}
          aria-describedby="submit-game-concept-description submit-game-concept-length submit-game-concept-guidance"
          aria-invalid={!!normalizedBigId && !isValidBigId}
        />
        <div id="submit-game-concept-guidance" className={styles.submissionGuidance}>
          <span>{guidance}</span>
        </div>
        <div className={styles.deleteModalActions}>
          <button type="button" className={styles.cancelButton} onClick={onClose}>
            {cancel}
          </button>
          <button
            type="button"
            className={styles.cancelButton}
            disabled={!canConfirm}
            onClick={() => {
              if (canConfirm) {
                onConfirm("");
              }
            }}
          >
            {submitWithoutId}
          </button>
          <button
            type="submit"
            className={styles.saveAndExitButton}
            disabled={!canConfirm || !isValidBigId}
          >
            {submit}
          </button>
        </div>
      </form>
    </Modal>
  );
};
